import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PortfolioSite } from '../api/portfolioApi';
import type { Entity, EntityConnection } from '../../types/api';

export interface SiteState {
  sitesByPortfolio: Record<string, PortfolioSite[]>;
  currentSite: PortfolioSite | null;
  // Entities and connections for the current site
  entities: Entity[];
  connections: EntityConnection[];
  isLoadingEntities: boolean;
  isLoadingConnections: boolean;
}

const STORAGE_KEY = 'current_site_v1';

// Load persisted site from localStorage
const loadPersistedSite = (): PortfolioSite | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as PortfolioSite;
    }
  } catch (error) {
    console.warn('Failed to load persisted site:', error);
  }
  return null;
};

// Save site to localStorage
const savePersistedSite = (site: PortfolioSite | null) => {
  try {
    if (site) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(site));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.warn('Failed to save persisted site:', error);
  }
};

const persistedSite = loadPersistedSite();

const initialState: SiteState = {
  sitesByPortfolio: {},
  currentSite: persistedSite,
  entities: [],
  connections: [],
  isLoadingEntities: false,
  isLoadingConnections: false,
};

const siteSlice = createSlice({
  name: 'site',
  initialState,
  reducers: {
    setSitesForPortfolio: (
      state,
      action: PayloadAction<{ portfolioId: string; sites: PortfolioSite[] }>
    ) => {
      const { portfolioId, sites } = action.payload;
      state.sitesByPortfolio[portfolioId] = sites;
    },
    setCurrentSite: (state, action: PayloadAction<PortfolioSite | null>) => {
      const previousSiteId = state.currentSite?.site_id;
      const newSiteId = action.payload?.site_id;
      
      state.currentSite = action.payload;
      // Persist to localStorage
      savePersistedSite(action.payload);
      
      // Clear entities and connections when site changes
      if (previousSiteId !== newSiteId) {
        state.entities = [];
        state.connections = [];
      }
    },
    setEntities: (state, action: PayloadAction<Entity[]>) => {
      state.entities = action.payload;
    },
    setConnections: (state, action: PayloadAction<EntityConnection[]>) => {
      state.connections = action.payload;
    },
    setLoadingEntities: (state, action: PayloadAction<boolean>) => {
      state.isLoadingEntities = action.payload;
    },
    setLoadingConnections: (state, action: PayloadAction<boolean>) => {
      state.isLoadingConnections = action.payload;
    },
    resetSites: (state) => {
      state.sitesByPortfolio = {};
      state.currentSite = null;
      state.entities = [];
      state.connections = [];
      state.isLoadingEntities = false;
      state.isLoadingConnections = false;
      // Clear persisted site from localStorage
      savePersistedSite(null);
    },
  },
});

export const {
  setSitesForPortfolio,
  setCurrentSite,
  setEntities,
  setConnections,
  setLoadingEntities,
  setLoadingConnections,
  resetSites,
} = siteSlice.actions;

export default siteSlice.reducer;


